import React, { PropsWithChildren, createContext, useContext, useRef } from 'react';
import { StoreApi } from 'zustand';
import { UseBoundStoreWithEqualityFn } from 'zustand/traditional';

export type ExtractState<S> = S extends {
    getState: () => infer T;
}
    ? T
    : never;
export type UseStore<S> = {
    <T>(selector: (state: S) => T): T;
    (): UseBoundStoreWithEqualityFn<StoreApi<ExtractState<StoreApi<any>>>>;
} & StoreApi<ExtractState<StoreApi<any>>>;
export function createProviderStore<Store extends StoreApi<any>>() {
    type S = ExtractState<Store>;
    type TStore = StoreApi<S>;

    const Context = createContext<UseBoundStoreWithEqualityFn<TStore> | null>(null);

    function useStoreApi() {
        const store = useContext(Context);
        if (!store) {
            console.trace('missing zustand provider in the tree');
            throw new Error('missing zustand provider in the tree');
        }
        return store;
    }

    type UseStore = {
        <T>(selector: (state: S) => T): T;
        (): UseBoundStoreWithEqualityFn<TStore>;
    } & TStore;

    /** 定义重载 */
    // function useStore<T>(selector: (state: S) => T): T;
    // function useStore(): UseBoundStoreWithEqualityFn<TStore>;

    /** 方法实现 */
    const useStore = (selector => {
        const store = useStoreApi();
        if (!selector) {
            return store;
        }
        return store(selector);
    }) as UseStore;

    function Provider(
        props: React.PropsWithChildren<{
            store: UseBoundStoreWithEqualityFn<TStore>;
        }>
    ) {
        const { children, store } = props;
        const storeRef = useRef(store).current;
        /** @deprecated 这里有问题，useStore是单例的， 给useStore挂载方法， 在Store多实例的时候， useStore的方法有问题 */
        useStore.getState = store.getState;
        /** @deprecated 这里有问题，useStore是单例的， 给useStore挂载方法， 在Store多实例的时候， useStore的方法有问题 */
        useStore.setState = store.setState;
        /** @deprecated 这里有问题，useStore是单例的， 给useStore挂载方法， 在Store多实例的时候， useStore的方法有问题 */
        useStore.subscribe = store.subscribe;
        return <Context.Provider value={storeRef}>{children}</Context.Provider>;
    }

    return {
        Provider,
        useStore,
        useStoreApi,
    };
}

export function createProviderStore2<Store extends StoreApi<any>>(factory: (...args: any[]) => Store) {
    const { Provider, useStore, useStoreApi } = createProviderStore<Store>();
    const ProviderComponent = (
        props: PropsWithChildren<{
            initState?: Record<string, any>;
            [key: string]: any;
        }>
    ) => {
        const store = useRef(factory(props.initState)).current;
        return <Provider store={store}>{props.children}</Provider>;
    };

    ProviderComponent.displayName = 'ProviderComponent';

    return {
        Provider,
        ProviderComponent,
        useStore,
        useStoreApi,
    };
}

// /**
//  * 组件上下文复用的Store
//  */
// export const ComponentStoreContext = createContext(null);

// type GetState<State> = { getState: StoreApi<State>['getState'] };
// type Selector<State> = (state: State) => Partial<State>;
// type SelectorState<State> = ReturnType<Selector<State>>;

// export function useComponentStore<State>(): GetState<State>;
// export function useComponentStore<State>(selector: Selector<State>): SelectorState<State>;

// export function useComponentStore<State>(selector?: Selector<State>): GetState<State> | SelectorState<State> {
//     const storeContext = useContext(ComponentStoreContext);
//     if (selector) {
//         return storeContext(selector) as SelectorState<State>;
//     }
//     return { getState: storeContext.getState } as GetState<State>;
// }

// export function useCreateComponentStore<T>(initState: T) {
//     const storeRef = useRef(
//         createWithEqualityFn<T>(() => ({
//             ...initState,
//         })),
//     );
//     return storeRef.current;
// }
